// Base de conocimiento (Manual): colecciones, subcategorias, busqueda en el navegador (respaldo del buscador del
// servidor), lo mas consultado, lo que falta revisar y el texto que se inserta en una respuesta. Funciones puras.
import type { Articulo } from '../api/conocimiento';
import { normalize } from './format';
import type { IconName } from './icons';
import { SOLO_ADJUNTOS, textoPlanoMarkdown } from './markdownKB';

export const SEP = ' / ';
export const partesDe = (categoria: string) =>
  categoria
    .split('/')
    .map((x) => x.trim())
    .filter(Boolean);

// ---------- Busqueda en el navegador ----------

// Todas las palabras deben aparecer en el titulo, la categoria, las etiquetas o el texto; el titulo pesa mas.
export function buscarArticulos<T extends Pick<Articulo, 'titulo' | 'categoria' | 'cuerpo'> & { etiquetas?: string[]; resumen?: string }>(lista: T[], q: string): T[] {
  const palabras = normalize(q).split(/\s+/).filter((p) => p.length > 1);
  if (!palabras.length) return lista;
  return lista
    .map((a) => {
      const t = normalize(a.titulo);
      const todo = `${t} ${normalize(a.categoria)} ${normalize((a.etiquetas ?? []).join(' '))} ${normalize(a.resumen ?? '')} ${normalize(a.cuerpo)}`;
      if (!palabras.every((p) => todo.includes(p))) return null;
      return { a, puntos: palabras.filter((p) => t.includes(p)).length };
    })
    .filter((x): x is { a: T; puntos: number } => !!x)
    .sort((x, y) => y.puntos - x.puntos)
    .map((x) => x.a);
}

// Fragmento con la primera palabra encontrada marcada con « » (como los del servidor).
export function fragmentoLocal(texto: string, q: string, largo = 160): string | null {
  const plano = textoPlanoMarkdown(texto).replace(/\s+/g, ' ');
  const palabras = normalize(q).split(/\s+/).filter((p) => p.length > 1);
  const n = normalize(plano);
  for (const p of palabras) {
    const i = n.indexOf(p);
    if (i < 0) continue;
    const desde = Math.max(0, i - 60);
    const pedazo = plano.slice(desde, desde + largo);
    const j = i - desde;
    return `${desde ? '…' : ''}${pedazo.slice(0, j)}«${pedazo.slice(j, j + p.length)}»${pedazo.slice(j + p.length)}${desde + largo < plano.length ? '…' : ''}`;
  }
  return null;
}

// ---------- Colecciones y subcategorias ----------

export type Tono = 'acc' | 'ok' | 'warn' | 'info' | 'bad';
export function iconoColeccion(nombre: string): { icono: IconName; tono: Tono } {
  const n = normalize(nombre);
  if (/\b3cx\b|central|telefon|pbx|extension|troncal/.test(n)) return { icono: 'phone', tono: 'acc' };
  if (/patton|gateway|dinstar|grandstream|fxo|fxs|\be1\b|router|red(es)?\b|firewall/.test(n)) return { icono: 'router', tono: 'info' };
  if (/instalac|cliente|empresa|sede/.test(n)) return { icono: 'building', tono: 'ok' };
  if (/comando|script|servidor|linux|vps|zammad|crm/.test(n)) return { icono: 'code', tono: 'bad' };
  if (/plantilla|respuesta|correo|cotizac|soporte/.test(n)) return { icono: 'doc', tono: 'warn' };
  return { icono: 'book', tono: 'acc' };
}

export type Coleccion = { nombre: string; total: number; detalle: string[] };

// Colecciones = primer segmento de la categoria. El detalle son las subcategorias mas grandes (o etiquetas si no hay).
export function colecciones(lista: Pick<Articulo, 'categoria' | 'etiquetas'>[]): Coleccion[] {
  const m = new Map<string, { total: number; subs: Map<string, number>; etiquetas: Map<string, number> }>();
  for (const a of lista) {
    const [raiz = '', sub] = partesDe(a.categoria);
    if (!raiz) continue;
    const c = m.get(raiz) ?? { total: 0, subs: new Map(), etiquetas: new Map() };
    c.total++;
    if (sub) c.subs.set(sub, (c.subs.get(sub) ?? 0) + 1);
    for (const x of a.etiquetas ?? []) c.etiquetas.set(x, (c.etiquetas.get(x) ?? 0) + 1);
    m.set(raiz, c);
  }
  return [...m.entries()]
    .map(([nombre, c]) => ({
      nombre,
      total: c.total,
      detalle: [...(c.subs.size ? c.subs : c.etiquetas).entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 3)
        .map(([x]) => x),
    }))
    .sort((a, b) => b.total - a.total || a.nombre.localeCompare(b.nombre));
}

// Dentro de una ruta ("3CX / Hosting"): sus subcategorias directas con cuantos articulos tienen (en total) y los
// articulos que estan justo en esa ruta.
export function contenidoDe<T extends Pick<Articulo, 'categoria'>>(lista: T[], ruta: string[]) {
  const subs = new Map<string, number>();
  const aqui: T[] = [];
  let total = 0;
  for (const a of lista) {
    const p = partesDe(a.categoria);
    if (ruta.some((x, i) => normalize(p[i] ?? '') !== normalize(x))) continue;
    total++;
    if (p.length === ruta.length) aqui.push(a);
    else subs.set(p[ruta.length], (subs.get(p[ruta.length]) ?? 0) + 1);
  }
  return { subcategorias: [...subs.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([nombre, n]) => ({ nombre, total: n })), articulos: aqui, total };
}

// Todas las rutas de categoria que existen (para autocompletar en el editor), con sus prefijos.
export function rutasExistentes(lista: Pick<Articulo, 'categoria'>[]) {
  const s = new Set<string>();
  for (const a of lista) {
    const p = partesDe(a.categoria);
    for (let i = 1; i <= p.length; i++) s.add(p.slice(0, i).join(SEP));
  }
  return [...s].sort((a, b) => a.localeCompare(b));
}

// ---------- Uso y calidad ----------

export const masConsultados = <T extends Pick<Articulo, 'vistas' | 'titulo'>>(lista: T[], n = 5) =>
  lista
    .filter((a) => a.vistas > 0)
    .sort((a, b) => b.vistas - a.vistas || a.titulo.localeCompare(b.titulo))
    .slice(0, n);

// Para revisar: venian de Zammad solo con adjuntos o casi no tienen texto.
export const MIN_LETRAS = 120;
export const porRevisar = (a: Pick<Articulo, 'cuerpo'>) => SOLO_ADJUNTOS.test(a.cuerpo) || textoPlanoMarkdown(a.cuerpo).replace(/\s+/g, ' ').trim().length < MIN_LETRAS;

// ---------- Insertar en una respuesta ----------

// Si el articulo es corto va completo (en texto plano); si no, el resumen (o su primer parrafo) y el enlace.
export const CORTO = 900;
export function textoParaRespuesta(a: Pick<Articulo, 'id' | 'titulo' | 'cuerpo' | 'resumen'>, origen: string) {
  const plano = textoPlanoMarkdown(a.cuerpo);
  if (plano.length <= CORTO && !SOLO_ADJUNTOS.test(a.cuerpo)) return plano;
  const resumen = a.resumen.trim() || plano.split(/\n{2,}/).find((p) => p.trim().length > 40)?.trim() || a.titulo;
  return `${resumen}\n\nMás detalle en «${a.titulo}»: ${origen}/bandeja/conocimiento?articulo=${a.id}`;
}
